import { Matrix4, Matrix3, Quaternion, Vector3 } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB.js'

/** OBB alineado con item.matrix (mismo criterio que setOBB en store). */
export function buildItemOBBFromMatrix(
  matrix: Matrix4,
  width: number,
  height: number,
  thickness: number,
  target?: OBB,
): OBB {
  const obb = target ?? new OBB()
  const centerPosition = new Vector3()
  const quaternion = new Quaternion()
  const scale = new Vector3()
  matrix.decompose(centerPosition, quaternion, scale)

  const itemCenter = new Vector3(width / 2, height / 2, thickness / 2)
  itemCenter.applyQuaternion(quaternion).add(centerPosition)

  const rotationMatrix4 = new Matrix4().makeRotationFromQuaternion(quaternion)
  const rotationMatrix = new Matrix3().setFromMatrix4(rotationMatrix4)

  obb.center.copy(itemCenter)
  obb.halfSize.set(width / 2, height / 2, thickness / 2)
  obb.rotation.copy(rotationMatrix)
  return obb
}

export function horizontalCenterDistance(a: OBB, b: OBB): number {
  return Math.hypot(a.center.x - b.center.x, a.center.z - b.center.z)
}

const ROW_SNAP_ROTATION_MATCH_RAD = 0.42

type RowSnapItemLike = {
  width?: number
  matrix?: { decompose: (p: Vector3, q: Quaternion, s: Vector3) => void }
}

/** Misma fórmula que collideItems para módulos rectos en fila (lateral X). */
export function computeStraightRowSnapTarget(
  anchor: RowSnapItemLike,
  draggedWidth: number,
  draggedPos: Vector3,
): Vector3 | null {
  if (!anchor.matrix) return null
  const anchorPos = new Vector3()
  const anchorQuat = new Quaternion()
  anchor.matrix.decompose(anchorPos, anchorQuat, new Vector3())
  const xAxis = new Vector3(1, 0, 0).applyQuaternion(anchorQuat)
  const xProj = xAxis.dot(draggedPos.clone().sub(anchorPos).setY(0))
  const anchorWidth = Number(anchor.width ?? 0) || 0
  const width = draggedWidth > 0 ? draggedWidth : anchorWidth
  if (Math.abs(xProj) < 1) return null
  const snapDirection = xProj < 0 ? 1 : -1
  const offset = snapDirection * (snapDirection < 0 ? width : anchorWidth)
  return anchorPos.clone().add(xAxis.multiplyScalar(offset))
}

export function areItemsRowAligned(
  a: RowSnapItemLike,
  b: RowSnapItemLike,
  maxAngleRad = ROW_SNAP_ROTATION_MATCH_RAD,
): boolean {
  if (!a.matrix || !b.matrix) return false
  const qa = new Quaternion()
  const qb = new Quaternion()
  a.matrix.decompose(new Vector3(), qa, new Vector3())
  b.matrix.decompose(new Vector3(), qb, new Vector3())
  return qa.angleTo(qb) <= maxAngleRad
}

/** Distancia 2D (mm) del origen arrastrado al punto de snap lateral en fila. */
export function straightModuleLateralSnapDistance(
  dragged: RowSnapItemLike,
  anchor: RowSnapItemLike,
  draggedPos: Vector3,
  draggedWidth = 0,
): number | null {
  if (!areItemsRowAligned(dragged, anchor)) return null
  const width = draggedWidth > 0 ? draggedWidth : Number(dragged.width ?? 0) || 0
  const target = computeStraightRowSnapTarget(anchor, width, draggedPos)
  if (!target) return null
  return Math.hypot(draggedPos.x - target.x, draggedPos.z - target.z)
}
